import { useCallback } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import type { CurrencyCode } from "web_myplann/Shared/constants";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { formatMillions } from "web_myplann/Shared/Utils/formatMillions";
import { getCurrencyLocale } from "web_myplann/Shared/Utils/getCurrencyLocale";

type FormatGoalAmountOptions = {
	withSymbol?: boolean;
};

export const formatGoalAmount = (
	amount: number,
	currencyCode: CurrencyCode | string | null | undefined,
	t: TranslateFunction,
	{ withSymbol = true }: FormatGoalAmountOptions = {},
): string => {
	const locale = getCurrencyLocale(currencyCode);
	const symbol =
		currencyCode && withSymbol
			? (getCurrencySymbol(currencyCode as CurrencyCode) ?? "")
			: "";
	const appendSymbol = (value: string) =>
		symbol ? `${value} ${symbol}` : value;

	if (amount >= 1_000_000) {
		const fractionDigits = amount % 1_000_000 === 0 ? 0 : 1;
		const formatted = formatMillions(amount, locale, fractionDigits);
		return appendSymbol(`${formatted} ${t(Resources.Goals.Amount.millions)}`);
	}

	if (amount >= 100_000) {
		const tis = Math.round(amount / 1000);
		return appendSymbol(`${tis} ${t(Resources.Goals.Amount.thousands)}`);
	}

	return appendSymbol(
		amount.toLocaleString(locale, { maximumFractionDigits: 0 }),
	);
};

export const useFormatGoalAmount = () => {
	const { t } = useResource();

	return useCallback(
		(
			amount: number,
			currencyCode?: CurrencyCode | string | null,
			options?: FormatGoalAmountOptions,
		): string => formatGoalAmount(amount, currencyCode, t, options),
		[t],
	);
};
